import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Card } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { HelpHint } from '@/components/HelpHint'
import { DomainsAPI, type TemplatePreview } from '@/api/domains'
import { MissionsAPI, type MissionTemplate } from '@/api/missions'
import {
  CommuneAPI,
  type KeeperCard,
  type KeeperProfile,
  type RoadmapVotesResponse,
  type SourceRequest,
} from '@/api/commune'
import { CHANGELOG } from '@/content/changelog'
import { communeRoadmap, voteKey, type CommuneRoadmap } from '@/lib/commune'
import './commune.css'

// Commune — Diwa's community region: a town square, not a separate app. It stitches together
// pieces that already live in the verse (the roadmap, the changelog, the template + recipe
// markets, the request affordances, the sigil wall) and adds a doorway INTO Eden, the social
// world that ships later. Three sections now read REAL backends: the roadmap vote persists a
// community tally (GET/POST /commune/roadmap-votes), Keepers is the live public-profile
// directory (GET /commune/keepers, membrane-gated), and Your requests lists the caller's own
// persisted source requests (GET /commune/requests). What still needs a surface we don't have
// (Eden itself, template/feature ticket-status) stays a clearly-marked "coming soon" or a
// mailto — never faked.

// Template + feature requests have no persisted intake yet, so they stay the SAME prefilled
// mailto the Domains gallery already ships — the real, existing mechanism, mirrored here.
const TEMPLATE_REQUEST_MAILTO =
  'mailto:ss@diwata.domains?subject=Template%20request&body=' +
  encodeURIComponent('The world I want to track:\n\nWhat lives in it (the things):\n\nWhat I want Diwa to do with it:\n')

const FEATURE_REQUEST_MAILTO =
  'mailto:ss@diwata.domains?subject=Feature%20request&body=' +
  encodeURIComponent('What I wish Diwa could do:\n\nWhen it would help:\n')

/** A stable react-query key for the whole votes read. The vote map is keyed by roadmap slug,
 *  so one query backs every card and one optimistic write updates them all in place. */
const VOTES_KEY = ['commune', 'roadmap-votes'] as const

// HONEST failure copy (P0-2, 2026-08 audit). There is no retry queue: a failed
// toggle is rolled back and LOST, so the copy may never claim a click "still
// registers". Exported so the tests can hold the words to that promise.
export const VOTE_TALLY_ERROR_COPY =
  "Couldn't load the vote tally — voting is unavailable right now. Refresh to try again."
export const VOTE_SAVE_ERROR_COPY = "That vote didn't save — try again."

/** The vote affordance — now REAL. Count + voted state come from the shared votes query;
 *  the click fires the toggle mutation (optimistic, then invalidate). It never invents a
 *  number: while the tally is loading it shows the bare button with no count. */
function VoteButton({
  count,
  voted,
  pending,
  onToggle,
}: {
  count: number | null
  voted: boolean
  pending: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      className={`dw-vote${voted ? ' dw-vote--on' : ''}`}
      aria-pressed={voted}
      disabled={pending}
      onClick={onToggle}
    >
      <span className="dw-vote-mark" aria-hidden="true">▲</span>
      {voted ? 'Voted' : 'Vote'}
      {count !== null && (
        <span className="dw-vote-count" aria-label={`${count} votes`}>
          {count}
        </span>
      )}
    </button>
  )
}

/** One votable roadmap item, wired to its slot in the shared vote map. */
function RoadmapVoteCard({
  title,
  note,
  count,
  voted,
  pending,
  onToggle,
}: {
  title: string
  note: string
  count: number | null
  voted: boolean
  pending: boolean
  onToggle: () => void
}) {
  return (
    <Card className="dw-commune-item">
      <div className="dw-commune-item-body">
        <div className="dw-commune-item-title">{title}</div>
        <p className="dw-commune-item-note">{note}</p>
      </div>
      <div className="dw-commune-item-foot">
        <VoteButton count={count} voted={voted} pending={pending} onToggle={onToggle} />
      </div>
    </Card>
  )
}

function WhatsNext({ roadmap }: { roadmap: CommuneRoadmap }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new CommuneAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()

  // Only the votable bands carry a key (in-progress items are shown but not votable).
  const votable = useMemo(() => [...roadmap.strong, ...roadmap.considering], [roadmap])
  const keys = useMemo(() => votable.map((it) => voteKey(it.title)), [votable])

  const votesQuery = useQuery({
    queryKey: [...VOTES_KEY, keys],
    queryFn: () => api.roadmapVotes(keys),
    enabled: keys.length > 0,
  })
  const votes = votesQuery.data?.votes

  // A failed toggle is rolled back and LOST (no retry queue) — say so, honestly.
  const [saveError, setSaveError] = useState<string | null>(null)

  const toggle = useMutation({
    mutationFn: (key: string) => api.toggleVote(key),
    onMutate: async (key: string) => {
      setSaveError(null)
      await qc.cancelQueries({ queryKey: [...VOTES_KEY, keys] })
      const prev = qc.getQueryData<RoadmapVotesResponse>([...VOTES_KEY, keys])
      qc.setQueryData<RoadmapVotesResponse>([...VOTES_KEY, keys], (old) => {
        const base = old?.votes ?? {}
        const cur = base[key] ?? { count: 0, voted: false }
        const nextVoted = !cur.voted
        const nextCount = Math.max(0, cur.count + (nextVoted ? 1 : -1))
        return { votes: { ...base, [key]: { count: nextCount, voted: nextVoted } } }
      })
      return { prev }
    },
    onError: (_e, _key, ctx) => {
      // roll the optimistic flip back AND admit it: the vote did not save. When
      // the cache was cold (the tally never loaded), there is no prev to restore
      // — drop the optimistic entry entirely rather than leave a phantom "Voted".
      if (ctx?.prev) qc.setQueryData([...VOTES_KEY, keys], ctx.prev)
      else qc.removeQueries({ queryKey: [...VOTES_KEY, keys], exact: true })
      setSaveError(VOTE_SAVE_ERROR_COPY)
    },
    onSuccess: () => setSaveError(null),
    onSettled: () => qc.invalidateQueries({ queryKey: [...VOTES_KEY, keys] }),
  })

  function cardProps(title: string) {
    const key = voteKey(title)
    const v = votes?.[key]
    return {
      count: v ? v.count : null,
      voted: v?.voted ?? false,
      pending: toggle.isPending && toggle.variables === key,
      onToggle: () => toggle.mutate(key),
    }
  }

  return (
    <section className="dw-commune-section">
      <div className="dw-section-header">
        <div>
          <div className="dw-section-title">
            What&apos;s next{' '}
            <HelpHint gloss="Diwa's direction, in the open — the near-term shortlist and what's further out. Vote to weigh in on what we build first." />
          </div>
          <span className="dw-inbox-detail">Weigh in on what we build first.</span>
        </div>
      </div>

      {roadmap.inProgress.length > 0 && (
        <div className="dw-commune-inflight">
          <span className="dw-commune-inflight-label">In the works now</span>
          <div className="dw-chip-row">
            {roadmap.inProgress.map((it) => (
              <HelpHint key={it.title} gloss={it.note}>
                <span className="dw-chip dw-chip--accent">{it.title}</span>
              </HelpHint>
            ))}
          </div>
        </div>
      )}

      <div className="dw-commune-band-label">Strong candidates</div>
      <div className="dw-commune-grid">
        {roadmap.strong.map((it) => (
          <RoadmapVoteCard key={it.title} title={it.title} note={it.note} {...cardProps(it.title)} />
        ))}
      </div>

      <div className="dw-commune-band-label">Under consideration</div>
      <div className="dw-commune-grid">
        {roadmap.considering.map((it) => (
          <RoadmapVoteCard key={it.title} title={it.title} note={it.note} {...cardProps(it.title)} />
        ))}
      </div>

      {votesQuery.isError && (
        <p className="dw-commune-vote-note" role="alert">
          {VOTE_TALLY_ERROR_COPY}
        </p>
      )}
      {saveError && (
        <p className="dw-commune-vote-note" role="alert">
          {saveError}
        </p>
      )}
    </section>
  )
}

function Shipped() {
  const recent = CHANGELOG.slice(0, 4)
  return (
    <section className="dw-commune-section">
      <div className="dw-section-header">
        <div>
          <div className="dw-section-title">
            Shipped{' '}
            <HelpHint gloss="Everything below runs in production today — the community's requests and the roadmap, made real." />
          </div>
          <span className="dw-inbox-detail">Live in production today.</span>
        </div>
        <Link to="/changelog" className="dw-inline-link">
          full changelog →
        </Link>
      </div>
      <div className="dw-commune-shipped">
        {recent.map((e) => (
          <Card key={`${e.date}-${e.title}`} className="dw-commune-shipped-entry">
            <div className="dw-commune-shipped-date">{e.date}</div>
            <div className="dw-commune-shipped-title">{e.title}</div>
            <ul className="dw-commune-shipped-points">
              {e.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </section>
  )
}

/** A sphere-chip row shared by both market card kinds. */
function TagChips({ tags }: { tags: string[] }) {
  if (tags.length === 0) return null
  return (
    <div className="dw-chip-row" style={{ marginTop: 8 }}>
      {tags.slice(0, 3).map((t) => (
        <span key={t} className="dw-chip">
          {t}
        </span>
      ))}
    </div>
  )
}

function Market() {
  const { diwaUrl } = useUI()
  const domainsApi = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const missionsApi = useMemo(() => new MissionsAPI(diwaUrl), [diwaUrl])

  const templates = useQuery({ queryKey: ['template-previews'], queryFn: () => domainsApi.templatePreviews() })
  const recipes = useQuery({ queryKey: ['mission-templates'], queryFn: () => missionsApi.listTemplates() })

  const shownTemplates: TemplatePreview[] = (templates.data?.templates ?? []).slice(0, 4)
  const shownRecipes: MissionTemplate[] = (recipes.data ?? []).slice(0, 4)

  return (
    <section className="dw-commune-section">
      <div className="dw-section-header">
        <div>
          <div className="dw-section-title">
            The market{' '}
            <HelpHint gloss="Shared, community-built starting points: domain templates (a world, pre-shaped) and mission recipes (a job Tala can run). Fork one, or request your own." />
          </div>
          <span className="dw-inbox-detail">Domain templates and mission recipes — fork one to start.</span>
        </div>
      </div>

      <div className="dw-commune-market-col">
        <div className="dw-commune-market-head">
          <span className="dw-commune-band-label" style={{ margin: 0 }}>
            Domain templates
          </span>
          <Link to="/domains?create=1" className="dw-inline-link">
            browse all →
          </Link>
        </div>
        {templates.isLoading && <div className="dw-rail-empty">Reading the template gallery…</div>}
        {templates.isError && <div className="dw-error">Couldn&apos;t load templates — try again shortly.</div>}
        <div className="dw-commune-grid">
          {shownTemplates.map((t) => (
            <Card key={t.key} className="dw-commune-item">
              <div className="dw-commune-item-body">
                <div className="dw-commune-item-title">{t.name}</div>
                <p className="dw-commune-item-note">{t.description}</p>
                <TagChips tags={[...t.entity_types].slice(0, 3)} />
              </div>
              <div className="dw-commune-item-foot">
                <Link to="/domains?create=1" className="dw-inline-link">
                  use template →
                </Link>
              </div>
            </Card>
          ))}
        </div>
        <div className="dw-inbox-detail" style={{ marginTop: 8 }}>
          Don&apos;t see your world?{' '}
          <a className="dw-inline-link" href={TEMPLATE_REQUEST_MAILTO}>
            Suggest a template →
          </a>
        </div>
      </div>

      <div className="dw-commune-market-col">
        <div className="dw-commune-market-head">
          <span className="dw-commune-band-label" style={{ margin: 0 }}>
            Mission recipes
          </span>
          <Link to="/missions" className="dw-inline-link">
            browse all →
          </Link>
        </div>
        {recipes.isLoading && <div className="dw-rail-empty">Reading the recipe registry…</div>}
        {recipes.isError && <div className="dw-error">Couldn&apos;t load recipes — try again shortly.</div>}
        <div className="dw-commune-grid">
          {shownRecipes.map((r) => (
            <Card key={r.key} className="dw-commune-item">
              <div className="dw-commune-item-body">
                <div className="dw-commune-item-title">{r.name}</div>
                <p className="dw-commune-item-note">{r.gloss}</p>
                <TagChips tags={r.tags} />
              </div>
              <div className="dw-commune-item-foot">
                <Link to="/missions" className="dw-inline-link">
                  use recipe →
                </Link>
              </div>
            </Card>
          ))}
        </div>
        <div className="dw-inbox-detail" style={{ marginTop: 8 }}>
          Missing a source your recipe needs?{' '}
          <Link className="dw-inline-link" to="/pipelines">
            Request a source →
          </Link>
        </div>
      </div>
    </section>
  )
}

/** A single request row: its status, name, when, and any detail the keeper left. */
function RequestRow({ req }: { req: SourceRequest }) {
  const when = new Date(req.created_at)
  const stamp = Number.isNaN(when.getTime())
    ? ''
    : when.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  return (
    <div className="dw-commune-req">
      <div className="dw-commune-req-main">
        <span className={`dw-commune-req-status dw-commune-req-status--${req.status.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
          {req.status.replace(/_/g, ' ')}
        </span>
        <div className="dw-commune-req-body">
          <div className="dw-commune-req-name">{req.name}</div>
          {req.detail && <div className="dw-commune-req-detail">{req.detail}</div>}
        </div>
      </div>
      <div className="dw-commune-req-meta">
        <span className="dw-commune-req-kind">{req.kind}</span>
        {stamp && <span className="dw-commune-req-when">{stamp}</span>}
      </div>
    </div>
  )
}

/** Your requests — now REAL. The caller's persisted source requests (GET /commune/requests)
 *  with their status. The request buttons below reach the live intake. Template + feature
 *  requests still have no persisted status surface, so they remain honest mailto links. */
function YourRequests() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new CommuneAPI(diwaUrl), [diwaUrl])
  const requests = useQuery({ queryKey: ['commune', 'requests'], queryFn: () => api.requests() })
  const rows = requests.data?.requests ?? []

  return (
    <section className="dw-commune-section">
      <div className="dw-section-header">
        <div>
          <div className="dw-section-title">
            Your requests{' '}
            <HelpHint gloss="What you've asked Diwa to reach — your source requests and where each one stands. Ask for more below." />
          </div>
          <span className="dw-inbox-detail">The sources you&apos;ve asked us to reach, and their status.</span>
        </div>
      </div>

      <Card className="dw-commune-loop">
        {requests.isLoading && <div className="dw-rail-empty">Reading your requests…</div>}
        {requests.isError && (
          <div className="dw-error">Couldn&apos;t load your requests — try again shortly.</div>
        )}
        {!requests.isLoading && !requests.isError && rows.length === 0 && (
          <div className="dw-commune-empty">
            <div className="dw-commune-empty-title">No requests yet.</div>
            <p className="dw-commune-empty-note">
              Ask Diwa to reach a source it doesn&apos;t cover, and it&apos;ll show up here with a
              status you can follow.
            </p>
          </div>
        )}
        {rows.length > 0 && (
          <div className="dw-commune-req-list">
            {rows.map((r) => (
              <RequestRow key={r.id} req={r} />
            ))}
          </div>
        )}

        <div className="dw-commune-request-row">
          <Link to="/pipelines" className="dw-btn">
            Request a source
          </Link>
          <a href={TEMPLATE_REQUEST_MAILTO} className="dw-btn">
            Request a template
          </a>
          <a href={FEATURE_REQUEST_MAILTO} className="dw-btn">
            Request a feature
          </a>
        </div>
        <p className="dw-commune-soon-note">
          Source requests are tracked here. Template and feature requests reach us by email — a
          tracked status for those opens later.
        </p>
      </Card>
    </section>
  )
}

/** A first-letter mark for a keeper with no avatar asset. */
function keeperInitial(name: string): string {
  const c = (name.trim().replace(/^(the |a )/i, '')[0] ?? '·').toUpperCase()
  return c
}

/** The full keeper view — a membrane-respecting drawer opened from a directory card. Reads
 *  GET /commune/keepers/{id}; only the sections the keeper made public come back, and goals
 *  never do. Honestly notes which sections are kept private. */
function KeeperDrawer({ keeper, onClose }: { keeper: KeeperCard; onClose: () => void }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new CommuneAPI(diwaUrl), [diwaUrl])
  const detail = useQuery({
    queryKey: ['commune', 'keeper', keeper.id],
    queryFn: () => api.keeper(keeper.id),
  })
  const p: KeeperProfile | undefined = detail.data

  return (
    <div className="dw-keeper-scrim" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="dw-keeper-drawer" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dw-keeper-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
        <div className="dw-keeper-drawer-head">
          <div className="dw-keeper-mark" aria-hidden="true">
            {keeperInitial(keeper.name)}
          </div>
          <div>
            <div className="dw-keeper-name">{p?.name ?? keeper.name}</div>
            {(p?.tagline || keeper.tagline) && (
              <div className="dw-keeper-archetype">{p?.tagline || keeper.tagline}</div>
            )}
          </div>
        </div>

        {detail.isLoading && <div className="dw-rail-empty">Opening this keeper&apos;s profile…</div>}
        {detail.isError && <div className="dw-error">Couldn&apos;t open this keeper right now.</div>}

        {p && (
          <div className="dw-keeper-drawer-body">
            {p.professional_summary && <p className="dw-keeper-summary">{p.professional_summary}</p>}

            {p.expertise.length > 0 && (
              <div className="dw-chip-row" style={{ marginTop: 4 }}>
                {p.expertise.slice(0, 8).map((x) => (
                  <span key={x} className="dw-chip">
                    {x}
                  </span>
                ))}
              </div>
            )}

            {p.visibility.sigils === 'public' ? (
              p.sigils.length > 0 ? (
                <div className="dw-keeper-drawer-sigils">
                  {p.sigils.map((s) => (
                    <HelpHint key={s.key} gloss={s.name}>
                      <span className="dw-keeper-sigil">{s.glyph}</span>
                    </HelpHint>
                  ))}
                </div>
              ) : (
                <div className="dw-keeper-sub">No sigils earned yet.</div>
              )
            ) : (
              <div className="dw-keeper-sub">Sigils kept private.</div>
            )}

            {p.visibility.progression === 'public' && p.progression ? (
              <div className="dw-keeper-stats">
                <span>
                  <strong>{p.progression.sigils_earned}</strong> sigils
                </span>
                <span>
                  <strong>{p.progression.streak_days}</strong> day streak
                </span>
                <span>
                  <strong>{p.progression.entities}</strong> tracked
                </span>
              </div>
            ) : (
              <div className="dw-keeper-sub">Progression kept private.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Keepers — now the LIVE public directory (GET /commune/keepers): every keeper who opened
 *  their identity to the commune, with the sigil glyphs they chose to show. Empty until
 *  someone opts in — an honest "be the first" pointing at your own Profile. */
function Keepers() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new CommuneAPI(diwaUrl), [diwaUrl])
  const dir = useQuery({ queryKey: ['commune', 'keepers'], queryFn: () => api.keepers() })
  const keepers = dir.data?.keepers ?? []
  const [open, setOpen] = useState<KeeperCard | null>(null)

  return (
    <section className="dw-commune-section">
      <div className="dw-section-header">
        <div>
          <div className="dw-section-title">
            Keepers{' '}
            <HelpHint gloss="The people of the verse. A keeper who makes their profile public shows their domains and the sigils they've earned. Goals always stay private." />
          </div>
          <span className="dw-inbox-detail">Public profiles, with the sigils they&apos;ve earned.</span>
        </div>
      </div>

      {dir.isLoading && <div className="dw-rail-empty">Reading the directory…</div>}
      {dir.isError && <div className="dw-error">Couldn&apos;t load the directory — try again shortly.</div>}

      {!dir.isLoading && !dir.isError && keepers.length === 0 && (
        <Card className="dw-commune-empty">
          <div className="dw-commune-empty-title">No public keepers yet.</div>
          <p className="dw-commune-empty-note">
            Be the first — open your profile to the commune from your{' '}
            <Link to="/profile" className="dw-inline-link">
              Profile
            </Link>
            . You choose exactly what crosses: identity, sigils, progression. Goals always stay
            yours.
          </p>
        </Card>
      )}

      {keepers.length > 0 && (
        <div className="dw-commune-keepers">
          {keepers.map((k) => (
            <Card key={k.id} className="dw-keeper dw-keeper--live" variant="raised">
              <button type="button" className="dw-keeper-open" onClick={() => setOpen(k)}>
                <div className="dw-keeper-mark" aria-hidden="true">
                  {keeperInitial(k.name)}
                </div>
                <div className="dw-keeper-body">
                  <div className="dw-keeper-name">{k.name}</div>
                  {k.tagline && <div className="dw-keeper-archetype">{k.tagline}</div>}
                  {k.sigils.length > 0 && (
                    <div className="dw-keeper-sigils" aria-hidden="true">
                      {k.sigils.map((g, i) => (
                        <span key={i} className="dw-keeper-sigil">
                          {g}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}

      {open && <KeeperDrawer keeper={open} onClose={() => setOpen(null)} />}
    </section>
  )
}

/** The Eden doorway — the founder's explicit ask. A distinct, elegant door INTO the social
 *  world that ships later. It reads as a real threshold, gracefully "soon". */
function EdenDoor() {
  return (
    <section className="dw-commune-section">
      <div className="dw-eden-door">
        <div className="dw-eden-door-inner">
          <div className="dw-eden-glyph" aria-hidden="true">
            ⟡
          </div>
          <div className="dw-eden-copy">
            <div className="dw-eden-eyebrow">
              The social world <Badge variant="info">Coming soon</Badge>
            </div>
            <div className="dw-eden-title">Eden</div>
            <p className="dw-eden-line">Where keepers meet keepers. Step out of your own verse and into the commons.</p>
          </div>
          <div className="dw-eden-cta">
            <button type="button" className="dw-btn dw-eden-enter" disabled aria-disabled="true">
              Enter Eden — soon
            </button>
            <a
              className="dw-inline-link"
              href="mailto:ss@diwata.domains?subject=Notify%20me%20when%20Eden%20opens"
            >
              Notify me →
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export function Commune() {
  const roadmap = useMemo(() => communeRoadmap(), [])
  return (
    <div className="dw-commune">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Commune{' '}
            <HelpHint gloss="Diwa's community region — a town square in the verse. Shape what Diwa becomes, see what shipped, meet other keepers, and step into Eden." />
          </div>
          <div className="dw-page-subtitle">
            Shape what Diwa becomes — vote on what&apos;s next, see what shipped, request what you
            need.
          </div>
        </div>
      </div>

      <WhatsNext roadmap={roadmap} />
      <Shipped />
      <Market />
      <YourRequests />
      <Keepers />
      <EdenDoor />
    </div>
  )
}
